import type { CompleteGameData, CompleteItemData, itemId, Drop, rangeLabel } from "./types";

type MergeRecipeData = NonNullable<CompleteItemData["merge"]>["recipes"][number];
type MergeRecipeSeed = Omit<MergeRecipeData, "count"> & { count: number };

// Standard merge recipes - 5 items = 2 next tier, 3 items = 1 next tier
const STANDARD_MERGE = [
  { count: 5, drops: { next: 2 }, causes: ["manual"] },
  { count: 3, drops: { next: 1 }, causes: ["manual"] },
] as const satisfies readonly MergeRecipeSeed[];

const AUTO_STANDARD_MERGE = [
  { count: 5, drops: { next: 2 }, causes: ["auto"] },
  { count: 3, drops: { next: 1 }, causes: ["auto"] },
] as const satisfies readonly MergeRecipeSeed[];

// Premium merge - better ratios for higher tiers
const PREMIUM_MERGE = [
  { count: 5, drops: { next: 3 }, causes: ["manual"] },
  { count: 3, drops: { next: 2 }, causes: ["manual"] },
] as const satisfies readonly MergeRecipeSeed[];

type ChainCtx = { tier: number; id: itemId; next: itemId | null };

/** Strip recipes/drops that reference `next` — for the last tier of a chain. */
function stripNext(item: Omit<CompleteItemData, "id" | "chain">): Omit<CompleteItemData, "id" | "chain"> {
  const out = { ...item };
  for (const k of ["merge", "tap", "charge"] as const) {
    const block = out[k] as { recipes: { drops: Drop }[] } | undefined;
    if (!block) continue;
    const recipes = block.recipes.filter((r) => r.drops.next == null);
    if (recipes.length) (out as Record<string, unknown>)[k] = { ...block, recipes };
    else delete out[k];
  }
  if (out.exhaust) {
    const drops = out.exhaust.drops.map(({ next: _, ...rest }) => rest as Drop);
    out.exhaust = { drops };
  }
  return out;
}

function chain<T, ID extends string>(
  chainId: ID,
  seeds: readonly T[],
  build: (seed: T, ctx: ChainCtx) => Omit<CompleteItemData, "id" | "chain">,
): Record<itemId, CompleteItemData> {
  return Object.fromEntries(
    Array.from({ length: seeds.length }, (_, i): [itemId, CompleteItemData] => {
      const tier = i + 1;
      const id = `${chainId}-${tier}` as itemId;
      const next = i + 1 < seeds.length ? (`${chainId}-${tier + 1}` as itemId) : null;
      const built = build(seeds[i], { tier, id, next });
      const finished = next === null ? stripNext(built) : built;
      return [id, { id, chain: { id: chainId as itemId, tier, next }, ...finished }];
    }),
  );
}

export const defaultGameData: CompleteGameData = {
  items: {
    // ===== BASIC MERGE CHAINS =====

    // Orb chain - explode on shade contact or tap
    ...chain<{ emoji: string; power: number; radius: rangeLabel }, "orb">("orb", [
      { emoji: "💧", power: 10,    radius: 1   },
      { emoji: "💦", power: 32,    radius: 1   },
      { emoji: "🌊", power: 100,   radius: 1.5 },
      { emoji: "🌀", power: 320,   radius: 1.5 },
      { emoji: "🔷", power: 1000,  radius: 2   },
      { emoji: "💠", power: 3200,  radius: 2.3 },
      { emoji: "💎", power: 10000, radius: 3   },
    ], (s) => ({
      display: { emoji: s.emoji },
      merge:   { recipes: [...AUTO_STANDARD_MERGE] },
      shade:   { explodeOnShade: { power: s.power, radius: s.radius } },
    })),

    // Gem chain - premium merge, redrop overflow
    // Placeholder emojis (test/treasure rewards) — 💎/💠/🔷 freed up for orb chain.
    ...chain("gem", [
      { emoji: "🟢" },
      { emoji: "🟡" },
      { emoji: "🔴" },
    ], (s) => ({
      display: { emoji: s.emoji },
      merge:   { recipes: [...PREMIUM_MERGE] },
    })),

    // ===== TAP WITH EXHAUST =====

    // Chest - exhausts HP, drops sequential items
    "chest-1": {
      id: "chest-1",
      display: { emoji: "📦" },
      tap: {
        recipes: [{ charge: null, exhaust: 1, drops: {}, dropRange: 9 }],
      },
      exhaust: {
        drops: Array(50).fill({ "orb-1": 1 }),
      },
    },
    "chest-2": {
      id: "chest-2",
      display: { emoji: "📦" },
      tap: {
        recipes: [{ charge: null, exhaust: 1, drops: {}, dropRange: 9 }],
      },
      exhaust: {
        drops: [{ "orb-1": 50 }],
      },
    },
    "chest-3": {
      id: "chest-3",
      display: { emoji: "🎁" },
      tap: {
        recipes: [{ charge: null, exhaust: 1, drops: {}, dropRange: 9 }],
      },
      exhaust: {
        drops: [{ "orb-1": 10 }],
      },
    },

    // ===== CHARGE WITH RECIPES =====

    // Flower - charges over time, paused in shade
    // Per-tier values derived from flowerTier (ctx.tier):
    //   orbTier     = max(1, flowerTier - 1)                            // F1/F2 → orb-1, F3 → orb-2, ...
    //   chargeCount = max(1, flowerTier - 1)                            // 1, 1, 2, 3, 4
    //   chargeUnit  = floor(2.5^(orbTier-1) / 2^(flowerTier-1) * 180)   // 180, 90, 112, 140, 175
    // Each tier produces 2x the orb-1/s throughput of the previous (test invariant).
    ...chain("flower", [
      { emoji: "🌱", auto: false }, // F1: manual tap only
      { emoji: "🌸", auto: true  },
      { emoji: "🌺", auto: true  },
      { emoji: "🌻", auto: true  },
      { emoji: "🪷", auto: true  },
    ], (s, { tier }) => {
      const orbTier = Math.max(1, tier - 1);
      const drop = `orb-${orbTier}` as itemId;
      const chargeCount = Math.max(1, tier - 1);
      const chargeUnit = Math.floor((2.5 ** (orbTier - 1) / 2 ** (tier - 1)) * 180);
      return {
        display: { emoji: s.emoji },
        merge:   { recipes: [...STANDARD_MERGE] },
        shade:   { pausedInShade: true },
        tap: {
          // Single chain recipe: one chargeUnit per drop, multiplied up to
          // floor(charge / chargeUnit) at tap time (capped by board fit).
          recipes: [{
            charge: chargeUnit,
            exhaust: null,
            drops: { [drop]: 1 },
            dropRange: s.auto ? 1.5 : 1,
            chain: true,
          }],
        },
        charge: {
          max: chargeUnit * chargeCount,
          // F1: tap-only.
          // F2/F3: regular auto at ring=1 (orth, anti-merge).
          // F4/F5: regular auto at ring=[1.5] (diagonals — anti-merge), plus a
          //   premium recipe at ring=[2] gated on full charge. Premium fires
          //   only when regular has been blocked long enough for charge to cap.
          recipes: !s.auto
            ? []
            : tier <= 3
            ? [{ charge: chargeUnit, drops: { [drop]: 1 }, dropRange: 1 }]
            : [
                { charge: chargeUnit,     drops: { [drop]: 1 }, dropRange: [1.5] },
                { charge: chargeUnit * 2, drops: { [drop]: 1 }, dropRange: [2], requiresMaxCharge: true },
              ],
        },
      };
    }),

    // ===== TAP WITH CHARGE REQUIREMENTS =====

    // Crystal - requires charge to tap, gives resources
    "crystal-1": {
      id: "crystal-1",
      display: { emoji: "🔮" },
      chain: { id: "crystal", tier: 1, next: "crystal-2" },
      merge: { recipes: [...STANDARD_MERGE] },
      tap: {
        recipes: [
          { charge: 120, exhaust: null, drops: { crystalEnergy: 5 }, dropRange: 9 },
          { charge: 240, exhaust: null, drops: { crystalEnergy: 15 }, dropRange: 9 },
        ],
      },
      charge: { max: 240, recipes: [] },
    },
    "crystal-2": {
      id: "crystal-2",
      display: { emoji: "💠" },
      chain: { id: "crystal", tier: 2, next: "crystal-3" },
      merge: { recipes: [...STANDARD_MERGE] },
      tap: {
        recipes: [
          { charge: 54, exhaust: null, drops: { crystalEnergy: 10 }, dropRange: 9 },
          { charge: 126, exhaust: null, drops: { crystalEnergy: 25 }, dropRange: 9 },
          { charge: 180, exhaust: null, drops: { crystalEnergy: 50, "gem-1": 1 }, dropRange: 9 },
        ],
      },
      charge: { max: 180, recipes: [] },
    },
    "crystal-3": {
      id: "crystal-3",
      display: { emoji: "⚡" },
      chain: { id: "crystal", tier: 3, next: null },
      tap: {
        recipes: [
          { charge: 24, exhaust: null, drops: { crystalEnergy: 20 }, dropRange: 9 },
          { charge: 60, exhaust: null, drops: { crystalEnergy: 50, "gem-2": 1 }, dropRange: 9 },
          { charge: 120, exhaust: null, drops: { crystalEnergy: 100, "gem-2": 2 }, dropRange: 9 },
        ],
      },
      charge: { max: 120, recipes: [] },
    },

    // ===== COMBINED: CHARGE + EXHAUST =====

    // Treasure chest - charges up, then exhausts when tapped
    "treasure-1": {
      id: "treasure-1",
      display: { emoji: "🗝️" },
      chain: { id: "treasure", tier: 1, next: "treasure-2" },
      merge: { recipes: [...STANDARD_MERGE] },
      tap: {
        recipes: [{ charge: 300, exhaust: 1, drops: {}, dropRange: 9 }],
      },
      exhaust: {
        drops: [
          { gold: 10, "gem-1": 1 },
          { gold: 25, "gem-1": 2 },
          { gold: 50, "gem-2": 1 },
        ],
      },
      charge: { max: 300, recipes: [] },
    },
    "treasure-2": {
      id: "treasure-2",
      display: { emoji: "💍" },
      chain: { id: "treasure", tier: 2, next: "treasure-3" },
      merge: { recipes: [...STANDARD_MERGE] },
      tap: {
        recipes: [
          { charge: 120, exhaust: null, drops: { gold: 5 }, dropRange: 9 },
          { charge: 240, exhaust: 1, drops: {}, dropRange: 9 },
        ],
      },
      exhaust: {
        drops: [
          { gold: 50, "gem-2": 1 },
          { gold: 100, "gem-2": 2 },
          { gold: 200, "gem-3": 1 },
        ],
      },
      charge: { max: 240, recipes: [] },
    },
    "treasure-3": {
      id: "treasure-3",
      display: { emoji: "👑" },
      chain: { id: "treasure", tier: 3, next: null },
      tap: {
        recipes: [
          { charge: 54, exhaust: null, drops: { gold: 10, crystalEnergy: 5 }, dropRange: 9 },
          { charge: 126, exhaust: null, drops: { gold: 25, crystalEnergy: 10 }, dropRange: 9 },
          { charge: 180, exhaust: 1, drops: {}, dropRange: 9 },
        ],
      },
      exhaust: {
        drops: [
          { gold: 200, "gem-3": 1, crystalEnergy: 20 },
          { gold: 500, "gem-3": 2, crystalEnergy: 50 },
          { gold: 1000, "gem-3": 3, crystalEnergy: 100 },
        ],
      },
      charge: { max: 180, recipes: [] },
    },

    // ===== SIMPLE TAP (NO CHARGE, NO EXHAUST) =====

    // Coin - instant tap, gives resources
    ...chain("coin", [
      { emoji: "🪙", gold: 1  },
      { emoji: "💰", gold: 5  },
      { emoji: "💵", gold: 25 },
    ], (s) => ({
      display: { emoji: s.emoji },
      merge:   { recipes: [...STANDARD_MERGE] },
      tap:     { recipes: [{ charge: null, exhaust: null, drops: { gold: s.gold }, dropRange: 9 }] },
    })),

    // ===== MULTI-TILE ITEMS =====

    // Large structures - take up multiple tiles
    ...chain<{ emoji: string; size: { x: number; y: number }; drops: Drop }, "castle">("castle", [
      { emoji: "🏰", size: { x: 2, y: 2 }, drops: { gold: 5 } },
      { emoji: "🏯", size: { x: 2, y: 2 }, drops: { gold: 15 } },
      { emoji: "🏛️", size: { x: 3, y: 3 }, drops: { gold: 50, crystalEnergy: 10 } },
    ], (s) => ({
      display: { emoji: s.emoji },
      size:    s.size,
      merge:   { recipes: [...STANDARD_MERGE] },
      tap:     { recipes: [{ charge: null, exhaust: null, drops: s.drops, dropRange: 9 }] },
    })),

    // Wide items
    ...chain("bridge", [
      { emoji: "🌉", size: { x: 3, y: 1 } },
      { emoji: "🌁", size: { x: 4, y: 1 } },
      { emoji: "🌉", size: { x: 5, y: 1 } },
    ], (s) => ({
      display: { emoji: s.emoji },
      size:    s.size,
      merge:   { recipes: [...STANDARD_MERGE] },
    })),

    // Tall items - charge over time, tap to release
    ...chain<{ emoji: string; size: { x: number; y: number }; max: number; drops: Drop }, "tower">("tower", [
      { emoji: "🗼", size: { x: 1, y: 2 }, max: 200, drops: { gold: 10 } },
      { emoji: "🗽", size: { x: 1, y: 3 }, max: 150, drops: { gold: 30 } },
      { emoji: "🗿", size: { x: 1, y: 4 }, max: 100, drops: { gold: 100, crystalEnergy: 20 } },
    ], (s) => ({
      display: { emoji: s.emoji },
      size:    s.size,
      merge:   { recipes: [...STANDARD_MERGE] },
      tap:     { recipes: [{ charge: s.max, exhaust: null, drops: s.drops, dropRange: 9 }] },
      charge:  { max: s.max, recipes: [] },
    })),

    // ===== SHADE EMITTERS =====
    // Emitters produce shade when covered, drop chests when killed

    "emitter-1": {
      id: "emitter-1",
      display: { emoji: "🌑" },
      shade: {
        spawnShade: {
          amount: 5, // +5 shade per shade tick
          enabledInShade: true,
          minShade: 50, // needs 50+ shade to be active
        },
      },
      tap: {
        recipes: [{ charge: null, exhaust: 1, drops: {}, dropRange: 9 }],
      },
      exhaust: {
        drops: [{ "chest-1": 1 }],
      },
    },
    "emitter-2": {
      id: "emitter-2",
      display: { emoji: "🌚" },
      shade: {
        spawnShade: {
          amount: 10, // +10 shade per shade tick
          enabledInShade: true,
          minShade: 100, // needs 100+ shade to be active
        },
      },
      tap: {
        recipes: [{ charge: null, exhaust: 1, drops: {}, dropRange: 9 }],
      },
      exhaust: {
        drops: [{ "chest-2": 1, "flower-1": 2 }],
      },
    },
    "emitter-3": {
      id: "emitter-3",
      display: { emoji: "🕳️" },
      shade: {
        spawnShade: {
          amount: 20, // +20 shade per shade tick
          enabledInShade: true,
          minShade: 150, // needs 150+ shade to be active
        },
      },
      tap: {
        recipes: [{ charge: null, exhaust: 1, drops: {}, dropRange: 9 }],
      },
      exhaust: {
        drops: [{ "chest-2": 2, "flower-2": 1, "crystal-1": 1 }],
      },
    },
  },
  animations: {
    "merge:collapse": { duration: 18 },
    "merge:spread": { duration: 12 },
    "item:spawn": { duration: 6 },
    "item:swapMove": { duration: 10 },
  },
};
